import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Search, Trash2, Image, Plus, Edit2, Upload, X } from 'lucide-react';
import { Button, Input, Select, Checkbox } from '../components/ui';
import { getEntityId } from '../utils/entityId';
import {
  CategoryPayload,
  deleteCategory,
  fetchAttributes as loadAttributes,
  fetchCategories as loadCategories,
  getApiErrorMessage,
  saveCategory,
  uploadAdminImage,
} from '../services/adminCatalog';

interface CategoryAttribute {
  attribute: string;
  isRequired: boolean;
  displayOrder: number;
}

interface Attribute {
  id: string;
  _id?: string;
  name: string;
  displayName: string;
  type: string;
  isActive: boolean;
}

interface Category {
  id: string;
  name: string;
  description?: string;
  imageUrl?: string;
  isActive: boolean;
  parent?: string | null;
  subcategories?: Category[];
  displayOrder?: number;
  attributes?: CategoryAttribute[];
  createdAt: string;
  updatedAt: string;
}

type CategoryFormState = {
  name: string;
  description: string;
  imageUrl: string;
  parent: string;
  isActive: boolean;
  selectedAttributes: CategoryAttribute[];
};

const emptyForm = (): CategoryFormState => ({
  name: '',
  description: '',
  imageUrl: '',
  parent: '',
  isActive: true,
  selectedAttributes: [],
});

const Categories: React.FC = () => {
  const { t } = useTranslation();
  const [categories, setCategories] = useState<Category[]>([]);
  const [attributes, setAttributes] = useState<Attribute[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingCategory, setEditingCategory] = useState<Category | null>(null);
  const [formData, setFormData] = useState<CategoryFormState>(emptyForm());
  const [submitting, setSubmitting] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [dragActive, setDragActive] = useState(false);

  const fetchCategories = async () => {
    try {
      setLoading(true);
      setCategories(await loadCategories<Category>());
    } catch (error: unknown) {
      console.error('Error fetching categories:', error);
      setCategories([]);
      alert(getApiErrorMessage(error, t('messages.errorSaving', { ns: 'categories', defaultValue: 'Failed to load categories' })));
    } finally {
      setLoading(false);
    }
  };

  const fetchAttributes = async () => {
    try {
      setAttributes(await loadAttributes<Attribute>(true));
    } catch (error: unknown) {
      console.error('Error fetching attributes:', error);
      setAttributes([]);
    }
  };

  useEffect(() => {
    (async () => {
      try {
        await Promise.all([fetchCategories(), fetchAttributes()]);
      } catch (error) {
        console.error('Error in useEffect:', error);
      }
    })();
  }, []);

  const filteredCategories = categories.filter(
    (category) =>
      searchTerm === '' ||
      category.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      category.description?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const parentOptions = categories
    .filter((c) => !editingCategory || getEntityId(c) !== getEntityId(editingCategory))
    .map((c) => ({ value: c.id, label: c.name }));

  const openCreateModal = () => {
    setEditingCategory(null);
    setFormData(emptyForm());
    setShowModal(true);
  };

  const openEditModal = (category: Category) => {
    setEditingCategory(category);
    setFormData({
      name: category.name,
      description: category.description || '',
      imageUrl: category.imageUrl || '',
      parent: category.parent ? String(category.parent) : '',
      isActive: category.isActive,
      selectedAttributes: (category.attributes || []).map((row, index) => ({
        attribute: String(row.attribute),
        isRequired: Boolean(row.isRequired),
        displayOrder: row.displayOrder ?? index,
      })),
    });
    setShowModal(true);
  };

  const toggleCategoryAttribute = (attributeId: string, checked: boolean) => {
    setFormData((prev) => {
      if (!checked) {
        return {
          ...prev,
          selectedAttributes: prev.selectedAttributes.filter((row) => row.attribute !== attributeId),
        };
      }
      if (prev.selectedAttributes.some((row) => row.attribute === attributeId)) {
        return prev;
      }
      return {
        ...prev,
        selectedAttributes: [
          ...prev.selectedAttributes,
          {
            attribute: attributeId,
            isRequired: false,
            displayOrder: prev.selectedAttributes.length,
          },
        ],
      };
    });
  };

  const setAttributeRequired = (attributeId: string, isRequired: boolean) => {
    setFormData((prev) => ({
      ...prev,
      selectedAttributes: prev.selectedAttributes.map((row) =>
        row.attribute === attributeId ? { ...row, isRequired } : row
      ),
    }));
  };

  const handleImageUpload = async (file: File) => {
    setUploadingImage(true);
    try {
      const fileUrl = await uploadAdminImage(file);
      setFormData((prev) => ({ ...prev, imageUrl: fileUrl }));
    } catch (error: unknown) {
      alert(getApiErrorMessage(error, t('messages.uploadFailed', { ns: 'categories' })));
    } finally {
      setUploadingImage(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      alert(t('messages.nameRequired', { ns: 'categories' }));
      return;
    }

    setSubmitting(true);
    try {
      const payload: CategoryPayload = {
        name: formData.name.trim(),
        description: formData.description.trim() || undefined,
        imageUrl: formData.imageUrl.trim() || undefined,
        parent: formData.parent || null,
        isActive: formData.isActive,
        attributes: formData.selectedAttributes,
      };

      const categoryId = editingCategory ? getEntityId(editingCategory) : undefined;
      await saveCategory(payload, categoryId);
      setShowModal(false);
      setEditingCategory(null);
      setFormData(emptyForm());
      await fetchCategories();
    } catch (error: unknown) {
      console.error('Error saving category:', error);
      alert(getApiErrorMessage(error, t('messages.errorSaving', { ns: 'categories', defaultValue: 'Failed to save category' })));
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (category: Category) => {
    if (window.confirm(t('messages.confirmDelete', { ns: 'categories', name: category.name }))) {
      try {
        const categoryId = getEntityId(category);
        if (!categoryId) {
          alert(t('messages.errorSaving', { ns: 'categories', defaultValue: 'Invalid category id' }));
          return;
        }
        await deleteCategory(categoryId);
        await fetchCategories();
      } catch (error: unknown) {
        console.error('Error deleting category:', error);
        alert(getApiErrorMessage(error, t('messages.errorDeleting', { ns: 'categories', defaultValue: 'Failed to delete category' })));
      }
    }
  };

  const handleToggleStatus = async (category: Category) => {
    try {
      const categoryId = getEntityId(category);
      if (!categoryId) return;
      await saveCategory({ name: category.name, isActive: !category.isActive }, categoryId);
      await fetchCategories();
    } catch (error: unknown) {
      console.error('Error updating category status:', error);
      alert(getApiErrorMessage(error, t('messages.errorSaving', { ns: 'categories', defaultValue: 'Failed to update category' })));
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const getAttributeName = (attributeId: string | unknown): string => {
    const id =
      typeof attributeId === 'object' && attributeId !== null
        ? getEntityId(attributeId as { id?: string; _id?: string })
        : String(attributeId);
    return attributes.find((a) => getEntityId(a) === id)?.displayName || id;
  };

  const getParentName = (parentId: string | null | undefined) => {
    if (!parentId) return null;
    const parent = categories.find((c) => c.id === String(parentId));
    return parent?.name || String(parentId);
  };

  const CategoryCard: React.FC<{ category: Category }> = ({ category }) => (
    <div className="card p-6 hover:shadow-card-hover transition-shadow">
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-start space-x-3 flex-1">
          <div className="w-16 h-16 bg-gray-100 rounded-lg flex items-center justify-center flex-shrink-0 overflow-hidden">
            {category.imageUrl ? (
              <img src={category.imageUrl} alt={category.name} className="w-full h-full object-cover" />
            ) : (
              <Image size={24} className="text-gray-400" />
            )}
          </div>

          <div className="flex-1 min-w-0">
            <div className="flex items-center space-x-2 mb-2">
              <h3 className="font-semibold text-gray-900 text-lg truncate">{category.name}</h3>
              {category.parent && (
                <span className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded-full">Sub</span>
              )}
            </div>
            {category.parent && (
              <p className="text-xs text-gray-500 mb-2">
                Parent: <span className="font-medium">{getParentName(category.parent)}</span>
              </p>
            )}
            {category.description && (
              <p className="text-gray-600 text-sm line-clamp-2 break-words">{category.description}</p>
            )}
          </div>
        </div>
      </div>

      <div className="flex flex-col space-y-3">
        <div className="flex items-center flex-wrap gap-2">
          <span className={`badge-${category.isActive ? 'green' : 'red'}`}>
            {category.isActive ? t('status.active', { ns: 'common' }) : t('status.inactive', { ns: 'common' })}
          </span>
          {categories.filter((c) => String(c.parent || '') === category.id).length > 0 && (
            <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded-full font-medium">
              {categories.filter((c) => String(c.parent || '') === category.id).length} sub
            </span>
          )}
          {category.attributes && category.attributes.length > 0 && (
            <span className="text-xs bg-purple-100 text-purple-800 px-2 py-1 rounded-full font-medium">
              {category.attributes.length} attribute{category.attributes.length > 1 ? 's' : ''}
            </span>
          )}
          <span className="text-xs text-gray-500">Created {formatDate(category.createdAt)}</span>
        </div>

        {category.attributes && category.attributes.length > 0 && (
          <div className="flex flex-wrap gap-1">
            {category.attributes.map((catAttr, idx) => (
              <span
                key={catAttr.attribute || `attr-${idx}`}
                className="text-xs bg-gray-100 text-gray-700 px-2 py-1 rounded"
                title={catAttr.isRequired ? 'Required' : 'Optional'}
              >
                {getAttributeName(catAttr.attribute)}
                {catAttr.isRequired && ' *'}
              </span>
            ))}
          </div>
        )}

        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Button
              onClick={() => handleToggleStatus(category)}
              variant={category.isActive ? 'outline' : 'secondary'}
              size="sm"
            >
              {category.isActive ? t('buttons.deactivate', { ns: 'common' }) : t('buttons.activate', { ns: 'common' })}
            </Button>
            <button
              onClick={() => openEditModal(category)}
              className="text-custom-primary hover:text-custom-primary-hover p-1"
              title={t('editCategory', { ns: 'categories' })}
            >
              <Edit2 size={16} />
            </button>
          </div>
          <button
            onClick={() => handleDelete(category)}
            className="text-red-600 hover:text-red-700 p-1"
            title={t('buttons.delete', { ns: 'common', defaultValue: 'Delete' })}
          >
            <Trash2 size={16} />
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">{t('title', { ns: 'categories' })}</h1>
          <p className="text-gray-600 mt-2">{t('subtitle', { ns: 'categories' })}</p>
        </div>
        <Button onClick={openCreateModal} icon={Plus} variant="primary">
          {t('addCategory', { ns: 'categories' })}
        </Button>
      </div>

      <div className="card p-6">
        <div className="max-w-md">
          <Input
            type="text"
            placeholder={t('search', { ns: 'categories' })}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            icon={Search}
            variant="search"
          />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {loading ? (
          <div className="col-span-full flex justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-custom-primary"></div>
          </div>
        ) : filteredCategories.length === 0 ? (
          <div className="col-span-full text-center py-12 text-gray-500">
            {searchTerm ? t('noSearchResults', { ns: 'categories' }) : t('table.noData', { ns: 'common' })}
          </div>
        ) : (
          filteredCategories.map((category) => <CategoryCard key={category.id} category={category} />)
        )}
      </div>

      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              {editingCategory ? t('modal.editTitle', { ns: 'categories' }) : t('modal.createTitle', { ns: 'categories' })}
            </h3>

            <form onSubmit={handleSubmit} className="space-y-4">
              <Input
                label={t('fields.name', { ns: 'categories' })}
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder={t('fields.namePlaceholder', { ns: 'categories' })}
                required
              />

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('fields.description', { ns: 'categories' })}
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="input-field w-full"
                  rows={3}
                  placeholder={t('fields.descriptionPlaceholder', { ns: 'categories' })}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('fields.image', { ns: 'categories' })}
                </label>
                <div
                  className={`border-2 border-dashed rounded-2xl p-6 text-center transition-colors ${
                    dragActive ? 'border-custom-primary bg-custom-secondary/20' : 'border-gray-300'
                  }`}
                  onDragEnter={(e) => {
                    e.preventDefault();
                    setDragActive(true);
                  }}
                  onDragLeave={(e) => {
                    e.preventDefault();
                    setDragActive(false);
                  }}
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={(e) => {
                    e.preventDefault();
                    setDragActive(false);
                    const file = e.dataTransfer.files?.[0];
                    if (file) handleImageUpload(file);
                  }}
                >
                  {formData.imageUrl ? (
                    <div className="relative inline-block">
                      <img
                        src={formData.imageUrl}
                        alt=""
                        className="max-h-32 rounded-lg mx-auto object-cover"
                      />
                      <button
                        type="button"
                        className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1"
                        onClick={() => setFormData((prev) => ({ ...prev, imageUrl: '' }))}
                      >
                        <X size={14} />
                      </button>
                    </div>
                  ) : (
                    <>
                      <Upload className="mx-auto text-gray-400 mb-2" size={32} />
                      <p className="text-sm text-gray-600">
                        {uploadingImage
                          ? t('fields.uploading', { ns: 'categories' })
                          : t('fields.uploadImage', { ns: 'categories' })}
                      </p>
                    </>
                  )}
                  <input
                    type="file"
                    accept="image/jpeg,image/png,image/gif,image/webp"
                    className="hidden"
                    id="category-image-upload"
                    disabled={uploadingImage}
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) handleImageUpload(file);
                      e.target.value = '';
                    }}
                  />
                  <label
                    htmlFor="category-image-upload"
                    className="mt-3 inline-block text-sm text-custom-primary cursor-pointer hover:underline"
                  >
                    {uploadingImage
                      ? t('fields.uploading', { ns: 'categories' })
                      : t('fields.uploadImage', { ns: 'categories' })}
                  </label>
                </div>
              </div>

              <Select
                label={t('fields.parent', { ns: 'categories' })}
                value={formData.parent}
                onChange={(e) => setFormData({ ...formData, parent: e.target.value })}
                options={[
                  { value: '', label: t('labels.noParent', { ns: 'categories' }) },
                  ...parentOptions,
                ]}
              />

              {attributes.length > 0 && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('fields.attributes', { ns: 'categories', defaultValue: 'Linked attributes' })}
                  </label>
                  <div className="space-y-2 max-h-48 overflow-y-auto border border-gray-200 rounded-xl p-3">
                    {attributes.map((attr) => {
                      const attrId = getEntityId(attr);
                      const selected = formData.selectedAttributes.find((row) => row.attribute === attrId);
                      return (
                        <div key={attrId} className="flex items-center justify-between gap-3">
                          <Checkbox
                            id={`cat-attr-${attrId}`}
                            checked={Boolean(selected)}
                            onChange={(e) => toggleCategoryAttribute(attrId, e.target.checked)}
                            label={attr.displayName}
                            variant="default"
                          />
                          {selected && (
                            <Checkbox
                              id={`cat-attr-req-${attrId}`}
                              checked={selected.isRequired}
                              onChange={(e) => setAttributeRequired(attrId, e.target.checked)}
                              label={t('fields.attributeRequired', { ns: 'categories', defaultValue: 'Required' })}
                              variant="default"
                            />
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              <Checkbox
                id="category-isActive"
                checked={formData.isActive}
                onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                label={t('fields.isActive', { ns: 'categories' })}
                variant="default"
              />

              <div className="flex justify-end space-x-3 pt-4">
                <Button type="button" onClick={() => setShowModal(false)} variant="outline" disabled={submitting}>
                  {t('buttons.cancel', { ns: 'common' })}
                </Button>
                <Button type="submit" disabled={submitting || !formData.name.trim()} variant="primary" loading={submitting}>
                  {editingCategory ? t('buttons.update', { ns: 'categories' }) : t('buttons.create', { ns: 'categories' })}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Categories;
